import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../lib/api";

interface BreakdownEntry {
  party: string | null;
  count: number;
}

interface PrecinctEntry {
  precinct: string | null;
  count: number;
}

interface Stats {
  totalVoters: number;
  totalVolunteers: number;
  activeSurveys: number;
  votersByParty: BreakdownEntry[];
  votersByPrecinct: PrecinctEntry[];
}

// Donut slice palette — primary first, then distinct hues that read in both
// light and dark mode.
const SLICE_COLORS = [
  "hsl(var(--primary))",
  "#3b82f6",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
  "#ec4899",
  "#64748b",
];

interface DonutSlice {
  label: string;
  count: number;
  color: string;
}

/** Pure-SVG donut chart: one stroked circle segment per slice. */
function DonutChart({ slices, total }: { slices: DonutSlice[]; total: number }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="w-36 h-36 shrink-0 -rotate-90">
        {slices.map((s) => {
          const fraction = total > 0 ? s.count / total : 0;
          const dash = fraction * circumference;
          const el = (
            <circle
              key={s.label}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={s.color}
              strokeWidth="14"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <ul className="space-y-1.5 text-sm min-w-0">
        {slices.map((s) => (
          <li key={s.label} className="flex items-center gap-2">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: s.color }}
            />
            <span className="truncate">{s.label}</span>
            <span className="text-muted-foreground ml-auto pl-3">
              {total > 0 ? Math.round((s.count / total) * 100) : 0}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch("/api/stats");
        if (!res.ok) {
          throw new Error(`Request failed (${res.status})`);
        }
        const data = (await res.json()) as Stats;
        if (active) {
          setStats(data);
        }
      } catch {
        if (active) {
          setError("Could not load dashboard stats.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      active = false;
    };
  }, []);

  // Collapse small parties into "Other" so the donut stays readable.
  const partySlices: DonutSlice[] = (() => {
    if (!stats) return [];
    const top = stats.votersByParty.slice(0, 6);
    const rest = stats.votersByParty.slice(6);
    const slices = top.map((p, i) => ({
      label: p.party ?? "Unknown",
      count: p.count,
      color: SLICE_COLORS[i % SLICE_COLORS.length] ?? "#64748b",
    }));
    const restCount = rest.reduce((sum, p) => sum + p.count, 0);
    if (restCount > 0) {
      slices.push({ label: "Other", count: restCount, color: "#94a3b8" });
    }
    return slices;
  })();

  const maxPrecinctCount =
    stats && stats.votersByPrecinct.length > 0
      ? Math.max(...stats.votersByPrecinct.map((p) => p.count))
      : 0;

  const statCards = stats
    ? [
        { label: "Total voters", value: stats.totalVoters },
        { label: "Total volunteers", value: stats.totalVolunteers },
        { label: "Active surveys", value: stats.activeSurveys },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Overview</h2>
        <p className="text-muted-foreground mt-1">
          Welcome back{user?.name ? `, ${user.name}` : ""}.
        </p>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {["Total voters", "Total volunteers", "Active surveys"].map((label) => (
            <div key={label} className="rounded-lg border bg-card p-5 space-y-1">
              <p className="text-sm text-muted-foreground">{label}</p>
              <p className="text-2xl font-bold text-muted-foreground/40">—</p>
              <p className="text-xs text-muted-foreground">Loading…</p>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border bg-card p-5">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : stats ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            {statCards.map((card) => (
              <div key={card.label} className="rounded-lg border bg-card p-5 space-y-1">
                <p className="text-sm text-muted-foreground">{card.label}</p>
                <p className="text-2xl font-bold">{card.value.toLocaleString()}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Party donut */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold">Party breakdown</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Registered voters by party affiliation.
                </p>
              </div>
              {partySlices.length === 0 ? (
                <p className="text-sm text-muted-foreground">No voter data yet.</p>
              ) : (
                <DonutChart slices={partySlices} total={stats.totalVoters} />
              )}
            </div>

            {/* Precinct bars */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold">Top precincts</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Where your voters are concentrated.
                </p>
              </div>
              {stats.votersByPrecinct.length === 0 ? (
                <p className="text-sm text-muted-foreground">No voter data yet.</p>
              ) : (
                <ul className="space-y-3">
                  {stats.votersByPrecinct.map((p) => {
                    const widthPct =
                      maxPrecinctCount > 0
                        ? Math.max(2, Math.round((p.count / maxPrecinctCount) * 100))
                        : 0;
                    return (
                      <li key={p.precinct ?? "__unknown__"} className="space-y-1">
                        <div className="flex items-baseline justify-between text-sm">
                          <span className="font-medium truncate">
                            {p.precinct ?? "No precinct"}
                          </span>
                          <span className="text-muted-foreground pl-3">
                            {p.count.toLocaleString()}
                          </span>
                        </div>
                        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
